import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Copy, Check, Play, RefreshCw, AlertCircle, Sparkles } from 'lucide-react'

// Supported languages for dropdowns
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fa', name: 'Persian' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'ar', name: 'Arabic' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'it', name: 'Italian' },
]

const LEVELS = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const

const LESSON_TYPES = [
  'introduction',
  'warmup',
  'vocabulary',
  'grammar',
  'practice',
  'learn',
  'checkpoint',
  'conversation',
] as const

type LessonType = (typeof LESSON_TYPES)[number]

// Sample presets for quick testing
const PRESETS = {
  a1Coffee: {
    name: 'A1 Coffee',
    lessonType: 'vocabulary' as LessonType,
    nativeLanguage: 'fa',
    targetLanguage: 'en',
    level: 'A1' as const,
    userName: 'Sara',
    isFirstSession: false,
    unitTask: 'Order coffee at a cafe',
    unitDescription: 'Practice ordering drinks and snacks',
    suggestedVocab: 'espresso, latte, croissant, please, thank you',
    suggestedGrammar: "I'd like + noun",
    vocabLessonIndex: 1,
    vocabLessonTotal: 1,
    grammarErrors: '',
    vocabularyGaps: '',
    learningStyle: '',
    interests: '',
  },
  b1Hotel: {
    name: 'B1 Hotel',
    lessonType: 'practice' as LessonType,
    nativeLanguage: 'es',
    targetLanguage: 'en',
    level: 'B1' as const,
    userName: 'Carlos',
    isFirstSession: false,
    unitTask: 'Check into a hotel',
    unitDescription: 'Handle hotel check-in including requests',
    suggestedVocab: 'reservation, check-in, key card, amenities',
    suggestedGrammar: 'Could you...?, Would it be possible to...?',
    vocabLessonIndex: 1,
    vocabLessonTotal: 1,
    grammarErrors: '',
    vocabularyGaps: '',
    learningStyle: '',
    interests: '',
  },
  a0Introduction: {
    name: 'A0 Intro',
    lessonType: 'introduction' as LessonType,
    nativeLanguage: 'ar',
    targetLanguage: 'en',
    level: 'A0' as const,
    userName: 'Ahmed',
    isFirstSession: true,
    unitTask: '',
    unitDescription: '',
    suggestedVocab: '',
    suggestedGrammar: '',
    vocabLessonIndex: 1,
    vocabLessonTotal: 1,
    grammarErrors: '',
    vocabularyGaps: '',
    learningStyle: '',
    interests: '',
  },
  b2Conversation: {
    name: 'B2 Conversation',
    lessonType: 'conversation' as LessonType,
    nativeLanguage: 'fa',
    targetLanguage: 'en',
    level: 'B2' as const,
    userName: 'Maryam',
    isFirstSession: false,
    unitTask: '',
    unitDescription: '',
    suggestedVocab: '',
    suggestedGrammar: '',
    vocabLessonIndex: 1,
    vocabLessonTotal: 1,
    grammarErrors: 'Confuses "since" and "for"\nOmits articles',
    vocabularyGaps: 'Struggles with phrasal verbs',
    learningStyle: 'Prefers concrete examples',
    interests: 'Software engineering\nHiking',
  },
  a0Learn: {
    name: 'A0 Learn',
    lessonType: 'learn' as LessonType,
    nativeLanguage: 'fa',
    targetLanguage: 'en',
    level: 'A0' as const,
    userName: 'Ali',
    isFirstSession: false,
    unitTask: 'Greet people',
    unitDescription: 'Learn basic greetings and introductions',
    suggestedVocab: 'hello, goodbye, my name is, nice to meet you',
    suggestedGrammar: 'I am + name',
    vocabLessonIndex: 1,
    vocabLessonTotal: 2,
    grammarErrors: '',
    vocabularyGaps: '',
    learningStyle: '',
    interests: '',
  },
}

type PresetKey = keyof typeof PRESETS

export function SessionTest() {
  const navigate = useNavigate()
  const { session, isLoading: isAuthLoading } = useAuth()

  // Form state
  const [lessonType, setLessonType] = useState<LessonType>('conversation')
  const [nativeLanguage, setNativeLanguage] = useState('fa')
  const [targetLanguage, setTargetLanguage] = useState('en')
  const [level, setLevel] = useState<(typeof LEVELS)[number]>('A1')
  const [userName, setUserName] = useState('')
  const [isFirstSession, setIsFirstSession] = useState(false)

  // Unit context
  const [unitTask, setUnitTask] = useState('')
  const [unitDescription, setUnitDescription] = useState('')
  const [suggestedVocab, setSuggestedVocab] = useState('')
  const [suggestedGrammar, setSuggestedGrammar] = useState('')
  const [vocabLessonIndex, setVocabLessonIndex] = useState(1)
  const [vocabLessonTotal, setVocabLessonTotal] = useState(1)

  // Memory (for conversation type)
  const [grammarErrors, setGrammarErrors] = useState('')
  const [vocabularyGaps, setVocabularyGaps] = useState('')
  const [learningStyle, setLearningStyle] = useState('')
  const [interests, setInterests] = useState('')

  // Output state
  const [generatedPrompt, setGeneratedPrompt] = useState<string | null>(null)
  const [charCount, setCharCount] = useState(0)
  const [tokenEstimate, setTokenEstimate] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Session state
  const [isStartingSession, setIsStartingSession] = useState(false)

  // Helper to check if unit context should be shown
  const showUnitContext = ['warmup', 'vocabulary', 'grammar', 'practice', 'learn'].includes(
    lessonType
  )

  // Helper to check if memory section should be shown
  const showMemory = lessonType === 'conversation'

  // Helper to check if vocab lesson fields should be shown
  const showVocabLessonFields = lessonType === 'vocabulary' || lessonType === 'learn'

  // Apply a preset
  const applyPreset = useCallback((presetKey: PresetKey) => {
    const preset = PRESETS[presetKey]
    setLessonType(preset.lessonType)
    setNativeLanguage(preset.nativeLanguage)
    setTargetLanguage(preset.targetLanguage)
    setLevel(preset.level)
    setUserName(preset.userName)
    setIsFirstSession(preset.isFirstSession)
    setUnitTask(preset.unitTask)
    setUnitDescription(preset.unitDescription)
    setSuggestedVocab(preset.suggestedVocab)
    setSuggestedGrammar(preset.suggestedGrammar)
    setVocabLessonIndex(preset.vocabLessonIndex)
    setVocabLessonTotal(preset.vocabLessonTotal)
    setGrammarErrors(preset.grammarErrors)
    setVocabularyGaps(preset.vocabularyGaps)
    setLearningStyle(preset.learningStyle)
    setInterests(preset.interests)
    // Clear previous output
    setGeneratedPrompt(null)
    setError(null)
  }, [])

  // Generate prompt
  const handleGeneratePrompt = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Build unit context if applicable
      let unitContext: Record<string, unknown> | null = null
      if (showUnitContext && unitTask) {
        unitContext = {
          task: unitTask,
          description: unitDescription || undefined,
          suggested_vocab: suggestedVocab
            ? suggestedVocab.split(',').map((v) => v.trim())
            : undefined,
          suggested_grammar: suggestedGrammar
            ? suggestedGrammar.split(',').map((g) => g.trim())
            : undefined,
        }
      }

      // Build memories if applicable
      let memories: Record<string, string[]> | null = null
      if (showMemory) {
        const memoryObj: Record<string, string[]> = {}
        if (grammarErrors) {
          memoryObj.grammar_errors = grammarErrors.split('\n').filter((e) => e.trim())
        }
        if (vocabularyGaps) {
          memoryObj.vocabulary_gaps = vocabularyGaps.split('\n').filter((v) => v.trim())
        }
        if (learningStyle) {
          memoryObj.learning_style = learningStyle.split('\n').filter((s) => s.trim())
        }
        if (interests) {
          memoryObj.interests = interests.split('\n').filter((i) => i.trim())
        }
        if (Object.keys(memoryObj).length > 0) {
          memories = memoryObj
        }
      }

      const response = await fetch('http://localhost:8001/preview-prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          native_language_id: nativeLanguage,
          target_language_id: targetLanguage,
          level,
          lesson_type: lessonType,
          user_name: userName || null,
          is_first_session: isFirstSession,
          unit_context: unitContext,
          vocab_lesson_index: vocabLessonIndex,
          vocab_lesson_total: vocabLessonTotal,
          memories,
          recent_sessions: null,
        }),
      })

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`)
      }

      const data = await response.json()
      setGeneratedPrompt(data.prompt)
      setCharCount(data.char_count)
      setTokenEstimate(data.estimated_tokens)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate prompt')
    } finally {
      setIsLoading(false)
    }
  }

  // Copy prompt to clipboard
  const handleCopyPrompt = async () => {
    if (!generatedPrompt) return
    try {
      await navigator.clipboard.writeText(generatedPrompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Start a real session with the configured parameters
  const handleStartSession = async () => {
    if (!session) {
      setError('You must be logged in to start a session')
      return
    }

    setIsStartingSession(true)
    setError(null)

    try {
      // Call livekit-token edge function
      const { data, error: fnError } = await supabase.functions.invoke('livekit-token', {
        body: {
          lesson_type: lessonType,
          // Note: unit_id would need to be a real unit from the user's roadmap
          // For test sessions, we typically don't pass a unit_id
        },
      })

      if (fnError || !data) {
        throw new Error(fnError?.message || 'Failed to get session token')
      }

      // Navigate to Practice page with the lesson type
      navigate('/practice', {
        state: {
          lessonType,
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session')
    } finally {
      setIsStartingSession(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-amber-500" />
              <h1 className="text-xl font-semibold text-stone-800">Session Prompt Tester</h1>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              Back to App
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Configuration */}
          <div className="space-y-6">
            {/* Lesson Type */}
            <section className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-4">
                Lesson Type
              </h2>
              <select
                value={lessonType}
                onChange={(e) => setLessonType(e.target.value as LessonType)}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {LESSON_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>
            </section>

            {/* Student Profile */}
            <section className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-4">
                Student Profile
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Native Language</label>
                    <select
                      value={nativeLanguage}
                      onChange={(e) => setNativeLanguage(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      {LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name} ({lang.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Target Language</label>
                    <select
                      value={targetLanguage}
                      onChange={(e) => setTargetLanguage(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      {LANGUAGES.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name} ({lang.code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Level</label>
                    <select
                      value={level}
                      onChange={(e) => setLevel(e.target.value as (typeof LEVELS)[number])}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      {LEVELS.map((lvl) => (
                        <option key={lvl} value={lvl}>
                          {lvl}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Name</label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Student name"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-stone-600">
                  <input
                    type="checkbox"
                    checked={isFirstSession}
                    onChange={(e) => setIsFirstSession(e.target.checked)}
                    className="rounded border-stone-300 text-amber-500 focus:ring-amber-500"
                  />
                  First session
                </label>
              </div>
            </section>

            {/* Unit Context - conditionally shown */}
            {showUnitContext && (
              <section className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-4">
                  Unit Context
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Task</label>
                    <input
                      type="text"
                      value={unitTask}
                      onChange={(e) => setUnitTask(e.target.value)}
                      placeholder="e.g., Order coffee at a cafe"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">Description</label>
                    <input
                      type="text"
                      value={unitDescription}
                      onChange={(e) => setUnitDescription(e.target.value)}
                      placeholder="Practice ordering drinks and snacks"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Vocabulary (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={suggestedVocab}
                      onChange={(e) => setSuggestedVocab(e.target.value)}
                      placeholder="espresso, latte, croissant"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Grammar (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={suggestedGrammar}
                      onChange={(e) => setSuggestedGrammar(e.target.value)}
                      placeholder="I'd like + noun, Can I have...?"
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                  {showVocabLessonFields && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">Lesson #</label>
                        <input
                          type="number"
                          min={1}
                          value={vocabLessonIndex}
                          onChange={(e) => setVocabLessonIndex(parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-stone-600 mb-1">of Total</label>
                        <input
                          type="number"
                          min={1}
                          value={vocabLessonTotal}
                          onChange={(e) => setVocabLessonTotal(parseInt(e.target.value) || 1)}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Memory - conditionally shown */}
            {showMemory && (
              <section className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-4">
                  Memory Context
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Grammar Errors (one per line)
                    </label>
                    <textarea
                      value={grammarErrors}
                      onChange={(e) => setGrammarErrors(e.target.value)}
                      placeholder={'Confuses "since" and "for"\nOmits articles'}
                      rows={2}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Vocabulary Gaps (one per line)
                    </label>
                    <textarea
                      value={vocabularyGaps}
                      onChange={(e) => setVocabularyGaps(e.target.value)}
                      placeholder="Struggles with phrasal verbs"
                      rows={2}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Learning Style (one per line)
                    </label>
                    <textarea
                      value={learningStyle}
                      onChange={(e) => setLearningStyle(e.target.value)}
                      placeholder="Prefers concrete examples"
                      rows={2}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-stone-600 mb-1">
                      Interests (one per line)
                    </label>
                    <textarea
                      value={interests}
                      onChange={(e) => setInterests(e.target.value)}
                      placeholder={'Software engineering\nHiking'}
                      rows={2}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleGeneratePrompt}
                disabled={isLoading}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              >
                {isLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Prompt
              </Button>
              <Button
                onClick={handleStartSession}
                disabled={isStartingSession || isAuthLoading || !session}
                variant="outline"
                className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                {isStartingSession ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Start Session
              </Button>
            </div>

            {isAuthLoading ? (
              <p className="text-sm text-stone-500 bg-stone-50 px-3 py-2 rounded-lg">
                Checking authentication...
              </p>
            ) : !session ? (
              <p className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                Log in to start a real session (prompt preview works without auth)
              </p>
            ) : null}
          </div>

          {/* Right Panel - Output */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <section className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="bg-stone-50 border-b border-stone-200 px-5 py-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide">
                  Generated Prompt
                </h2>
                {generatedPrompt && (
                  <button
                    onClick={handleCopyPrompt}
                    className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-800 transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-emerald-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="p-5">
                {error && (
                  <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-lg mb-4">
                    <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-rose-800">Error</p>
                      <p className="text-sm text-rose-600 mt-1">{error}</p>
                    </div>
                  </div>
                )}

                {generatedPrompt ? (
                  <>
                    <div className="bg-stone-50 rounded-lg p-4 max-h-[600px] overflow-y-auto">
                      <pre className="text-sm text-stone-700 whitespace-pre-wrap font-mono leading-relaxed">
                        {generatedPrompt}
                      </pre>
                    </div>
                    <div className="flex items-center justify-between mt-4 text-sm text-stone-500">
                      <span>
                        Chars: <span className="font-mono">{charCount.toLocaleString()}</span>
                      </span>
                      <span>
                        Tokens: <span className="font-mono">~{tokenEstimate.toLocaleString()}</span>
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-stone-400">
                    <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Configure parameters and click "Generate Prompt"</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* Presets */}
        <section className="mt-8 bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-stone-700 uppercase tracking-wide mb-4">
            Quick Presets
          </h2>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
              <Button
                key={key}
                variant="outline"
                size="sm"
                onClick={() => applyPreset(key)}
                className="border-stone-300 hover:border-amber-400 hover:bg-amber-50"
              >
                {PRESETS[key].name}
              </Button>
            ))}
          </div>
        </section>

        {/* Usage Instructions */}
        <section className="mt-8 bg-amber-50 rounded-xl border border-amber-200 p-5">
          <h2 className="text-sm font-semibold text-amber-800 uppercase tracking-wide mb-3">
            How to Use
          </h2>
          <div className="text-sm text-amber-700 space-y-2">
            <p>1. Start the prompt server:</p>
            <code className="block bg-amber-100 px-3 py-2 rounded font-mono text-xs">
              cd agent && PYTHONPATH=src uvicorn src.prompt_server:app --port 8001 --reload
            </code>
            <p className="mt-3">2. Configure parameters and generate the prompt to preview</p>
            <p>3. Click "Start Session" to begin a real practice session with the selected lesson type</p>
          </div>
        </section>
      </main>
    </div>
  )
}
